import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Student } from '@/interface/types'
import { api } from '@/lib/axios'

/**
 * Custom hook for managing student data
 * @returns Object containing student data and mutation functions
 */
export default function useStudent() {
  const queryClient = useQueryClient()

  const {
    data: students = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['students'],
    queryFn: async () => {
      // HTTP-only cookies sent automatically via api instance (withCredentials: true)
      const response = await api.get<Student[]>('/student')
      return response.data
    },
  })

  const createStudent = useMutation({
    mutationFn: async (newStudent: Partial<Student>) => {
      // HTTP-only cookies sent automatically via api instance (withCredentials: true)
      const response = await api.post<Student>('/student', newStudent)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['students'] })
    },
  })

  const updateStudent = useMutation({
    mutationFn: async ({ id, ...data }: Partial<Student> & { id: string }) => {
      // HTTP-only cookies sent automatically via api instance (withCredentials: true)
      const response = await api.patch<Student>(`/student/${id}`, data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['students'] })
    },
  })

  const deleteStudent = useMutation({
    mutationFn: async (id: string) => {
      // HTTP-only cookies sent automatically via api instance (withCredentials: true)
      await api.delete(`/student/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['students'] })
    },
  })

  return {
    students,
    isLoading,
    isError,
    error,
    createStudent: createStudent.mutate,
    updateStudent: updateStudent.mutate,
    deleteStudent: deleteStudent.mutate,
    isCreating: createStudent.isPending,
    isUpdating: updateStudent.isPending,
    isDeleting: deleteStudent.isPending,
  }
}
